'use client'

import { Button } from '@/components/ui/button'
import { ToastAction } from '@/components/ui/toast'
import { useToast } from '@/hooks/use-toast'
import { Confetti } from '@/lib/confetti'
import { Mutation } from '@/lib/generated/graphql-types'
import { useMutation } from '@apollo/client'
import gql from 'graphql-tag'
import { ThumbsUp } from 'lucide-react'
import { useEffect, useId } from 'react'
import { LIKE_SALARY } from '../queries'

const LIKE_BUTTON_ID = 'like-button'

const UPDATE_FRAGMENT = gql`
  fragment _ on SalaryType {
    userLikes
  }
`

export function LikeSalaryButton({
  salaryId,
  likes,
  className,
  onClick,
}: {
  salaryId: string
  likes: number
  className?: string
  onClick?: () => void
}) {
  const { toast } = useToast()
  const id = useId()

  const [likeSalary, { loading }] = useMutation<Pick<Mutation, 'likeSalary'>>(
    LIKE_SALARY,
    {
      variables: { input: { salaryId } },
      update: (cache, { data }) => {
        if (!data) {
          return
        }

        // update salary with new count
        const { userLikes, salaryId } = data.likeSalary
        cache.writeFragment({
          id: cache.identify({
            __typename: 'SalaryType',
            salaryId: salaryId,
          }),
          fragment: UPDATE_FRAGMENT,
          data: {
            userLikes: userLikes,
          },
        })
      },
      onError: () => {
        toast({
          title: 'Already liked',
          description: `You can like a salary only once.`,
          action: (
            <ToastAction
              altText="Try again"
              className="bg-button-primary-bg text-button-primary-text"
            >
              OK
            </ToastAction>
          ),
        })
      },
    },
  )

  useEffect(() => {
    const confetti = new Confetti(`${LIKE_BUTTON_ID}-${id}`)
    confetti.setCount(30)
    confetti.setSize(1)
    confetti.setPower(10)
    confetti.setFade(false)
    confetti.destroyTarget(false)
  }, [id])

  const handleLikeSalary = () => {
    void likeSalary()
    onClick?.()
  }

  return (
    <Button
      variant="ghost"
      className="w-fit px-3"
      id={`${LIKE_BUTTON_ID}-${id}`}
      onClick={handleLikeSalary}
      disabled={loading}
    >
      <ThumbsUp className={className} />
      <span className="ml-1 hidden xl:block">Like ({likes})</span>
      <span className="ml-1 block xl:hidden">({likes})</span>
    </Button>
  )
}
